import React, { FC, useCallback, useMemo } from 'react';

import { UseFormReturn } from 'react-hook-form';

import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ProductsSequenceProduct } from 'common/contracts';
import { EProductType } from 'constants/enums';
import {
  GeneralSection,
  SegmentsSection
} from 'views/Offers/components/sections';
import { IOfferDesignOption } from 'views/Offers/hooks/useOfferDesignOptions';
import { ProductEntry } from 'views/Offers/Popups/Form/hooks/usePopupProducts';

import FormSectionsTabs from 'components/FormSectionsTabs';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import {
  FormControl,
  FormField,
  FormItem
} from 'components/ui';
import { Separator } from 'components/ui/separator';
import { Tabs, TabsContent } from 'components/ui/tabs';

import { getPriorityOptions, PRIORITY_NONE_VALUE } from '../../../Popups/Form/PopupsForm.consts';
import { ProductsSection } from '../../../Popups/Form/components/Sections/ProductsSection';
import { DailyBonusFormFields } from '../DailyBonusForm.consts';

interface IDailyBonusContentProps {
  form: UseFormReturn<DailyBonusFormFields>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  offerDesignOptions: IOfferDesignOption[];
  isNameTaken: boolean;
  getProducts: UseQueryResult;
  productsList: ProductEntry[];
  addProduct: (product: ProductsSequenceProduct) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (id: string, product: ProductsSequenceProduct) => void;
  formatProductQuantity: UseMutationResult<
    unknown,
    unknown,
    { quantity: string; productType: EProductType },
    unknown
  >;
}

const DailyBonusContent: FC<IDailyBonusContentProps> = ({
  form,
  activeTab,
  setActiveTab,
  offerDesignOptions,
  isNameTaken,
  getProducts,
  productsList,
  addProduct,
  deleteProduct,
  updateProduct,
  formatProductQuantity
}): React.ReactElement => {
  const priorityOptions = useMemo(() => getPriorityOptions(), []);

  const renderPriorityField = useCallback(
    ({ field: { value, onChange } }: { field: { value: number | null; onChange: (...event: unknown[]) => void } }): React.ReactElement => {
      const dropdownValue = value === null ? PRIORITY_NONE_VALUE : value;
      const selectedPriority = priorityOptions.find(
        (option) => option.value === dropdownValue
      );
      const handlePriorityChange = (newValue: string | number): void => {
        onChange(newValue === PRIORITY_NONE_VALUE ? null : newValue);
      };
      return (
        <FormItem>
          <FormControl>
            <InputSelectDropdown
              key={String(dropdownValue)}
              label="Priority"
              placeholder="Enter Number"
              defaultSelected={selectedPriority}
              options={priorityOptions}
              onOptionChange={handlePriorityChange}
            />
          </FormControl>
        </FormItem>
      );
    },
    [priorityOptions]
  );

  const handleSegmentsChange = useCallback(
    (segments: string[]): void => {
      form.setValue('segments', segments, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <FormSectionsTabs
        currentTab={activeTab}
        tabs={[
          { value: 'settings', label: 'Settings' },
          { value: 'configuration', label: 'Configuration' }
        ]}
      />
      <TabsContent
        value="settings"
        forceMount
        className="mt-9 flex flex-col gap-y-9 data-[state=inactive]:hidden"
      >
        <GeneralSection
          offerDesignOptions={offerDesignOptions}
          isNameTaken={isNameTaken}
          hidePrice
        >
          <div className="grid grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="priority"
              render={renderPriorityField}
            />
          </div>
        </GeneralSection>
      </TabsContent>
      <TabsContent
        value="configuration"
        forceMount
        className="mt-9 flex flex-col gap-y-5 data-[state=inactive]:hidden"
      >
        <SegmentsSection
          items={form.watch('segments') || []}
          onItemsChange={handleSegmentsChange}
        />
        <Separator className="bg-base-border" />
        <ProductsSection
          form={form as any}
          isDailyBonus
          formatProductQuantity={formatProductQuantity}
          products={getProducts}
          productsList={productsList}
          addProduct={addProduct}
          deleteProduct={deleteProduct}
          updateProduct={updateProduct}
        />
      </TabsContent>
    </Tabs>
  );
};

export { DailyBonusContent };
